import {
  type UserSessionListInput,
  type UserSessionListSuccess,
  type UserSessionRevokeInput,
  type UserSessionRevokeSuccess,
  type UserId,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { useTantalimMutation } from '../mutation'
import { userSessionListKey } from '../keys'
import { type GetHookResponse, type RemoveHookResponse, mutationRemove } from '../hooks'

export type LoginSession = UserSessionListSuccess['sessions'][number]

export function useUserSessions(enabled: boolean, userId: UserId): GetHookResponse<LoginSession[]> {
  return useTantalimQuery<UserSessionListInput, UserSessionListSuccess, LoginSession[]>({
    url: '/user/session/list',
    queryKey: userSessionListKey(userId),
    input: {},
    convertResponse: ({ sessions }) => sessions,
    options: { enabled },
  })
}

export function useRevokeUserSessions(userId: UserId): RemoveHookResponse<UserSessionRevokeInput> {
  const queryClient = useQueryClient()
  const mutation = useTantalimMutation<UserSessionRevokeInput, UserSessionRevokeSuccess>({
    url: '/user/session/revoke',
  })

  async function remove(input: UserSessionRevokeInput): Promise<void> {
    await mutation.mutateAsync(input)
    queryClient.setQueryData<LoginSession[]>(userSessionListKey(userId), (sessions) => {
      if (!input.sessionIds) return []
      return sessions?.filter(({ sessionId }) => !input.sessionIds?.includes(sessionId))
    })
  }

  return mutationRemove(mutation, remove)
}
